import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  PersonAdd as PersonAddIcon,
  RateReview as RateReviewIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

const getMatchStatusDisplay = (status, confidence) => {
  // confidence is already stored as percentage (0-100), not decimal (0-1)
  const confidencePercent = confidence ? parseFloat(confidence).toFixed(1) : null;
  
  switch (status) {
    case 'exact':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip 
            label="Matched"
            color="success"
            size="small"
            icon={<CheckCircleIcon />}
          />
          {confidencePercent && (
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {confidencePercent}%
            </Typography>
          )}
        </Box>
      );
    case 'fuzzy_review':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip 
            label="Review"
            color="warning"
            size="small"
          />
          {confidencePercent && (
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {confidencePercent}%
            </Typography>
          )}
        </Box>
      );
    case 'new_lead':
      return (
        <Chip 
          label="New Lead"
          size="small"
          icon={<PersonAddIcon />}
          sx={{ 
            backgroundColor: '#e3f2fd', 
            color: '#1976d2',
            '& .MuiChip-icon': { color: '#1976d2' }
          }}
        />
      );
    case 'admin_confirmed':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip 
            label="Matched"
            color="success"
            size="small"
            icon={<CheckCircleIcon />}
          />
          {confidencePercent && (
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {confidencePercent}%
            </Typography>
          )}
        </Box>
      );
    case 'admin_corrected':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip 
            label="Matched"
            color="success"
            size="small"
            icon={<CheckCircleIcon />}
          />
          {confidencePercent && (
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {confidencePercent}%
            </Typography>
          )}
        </Box>
      );
    case 'skipped':
      return (
        <Chip 
          label="Skipped"
          size="small"
          sx={{ backgroundColor: '#f5f5f5', color: '#757575' }}
        />
      );
    case 'pending':
      return (
        <Chip 
          label="Pending"
          size="small"
          sx={{ backgroundColor: '#f5f5f5', color: '#9e9e9e' }}
        />
      );
    default:
      return (
        <Chip 
          label={status || 'Unknown'}
          size="small"
          sx={{ backgroundColor: '#f5f5f5' }}
        />
      );
  }
};

const formatCurrency = (value, currency = 'SGD') => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-SG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const GeBIZAwardsTable = ({ 
  // Data props (from parent API call)
  awards = [],
  loading = false,
  error = null,
  // Pagination props (controlled by parent)
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  // Search props
  searchQuery = '',
  onSearchChange,
  // Filter props
  matchStatusFilter,
  onFilterChange,
  fuzzyReviewCount = 0,
  totalCount = 0,
  // Callbacks
  onRefresh,
  onReviewClick,
}) => {
  // This is now a presentational component - all state and API calls are in parent

  if (error) {
    return (
      <Alert severity="error">
        Failed to load awards: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Search and Refresh Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by title, company, or quotation..."
          value={searchQuery}
          onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
          sx={{ minWidth: 350 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={matchStatusFilter || ''}
            onChange={(e) => onFilterChange?.(e.target.value || '')}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="exact">Matched</MenuItem>
            <MenuItem value="fuzzy_review">
              Needs Review{fuzzyReviewCount > 0 ? ` (${fuzzyReviewCount})` : ''}
            </MenuItem>
            <MenuItem value="new_lead">New Lead</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="skipped">Skipped</MenuItem>
            <MenuItem value="admin_confirmed">Admin Confirmed</MenuItem>
            <MenuItem value="admin_corrected">Admin Corrected</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>Quotation No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 250 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Agency</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Awarded To</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }} align="right">Value</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Awarded</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>Match Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 60 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {awards.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {searchQuery ? 'No awards match your search.' : 'No awards found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  awards.map((award) => (
                    <TableRow 
                      key={award.id}
                      hover
                      sx={{ '&:hover': { backgroundColor: '#fafafa' } }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#1976d2', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {award.quotation_no || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={award.title || ''}>
                          <Typography variant="body2" sx={{ 
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: '#1976d2',
                          }}>
                            {award.title || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={award.agency_awarding || ''}>
                          <Typography variant="body2" sx={{
                            maxWidth: 180,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {award.agency_awarding || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {award.awarded_to_company || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(award.award_value, award.award_currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(award.awarded_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getMatchStatusDisplay(award.match_status, award.match_confidence)}
                      </TableCell>
                      <TableCell>
                        {['fuzzy_review', 'new_lead', 'exact'].includes(award.match_status) && (
                          <Tooltip title="Review Match">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => onReviewClick && onReviewClick(award)}
                            >
                              <RateReviewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={onPageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Awards per page:"
              showFirstButton
              showLastButton
            />
          </>
        )}
      </TableContainer>
    </Box>
  );
};

export default GeBIZAwardsTable;
